import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSun, FaMoon, FaBars, FaTimes } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const EASE = [0.22, 1, 0.36, 1];

export default function Header() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const [isMobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const TOKENS = useMemo(
    () =>
      isDark
        ? {
            surface: 'rgba(10,15,25,0.65)',
            surfaceSolid: 'rgba(10,15,25,0.92)',
            border: 'rgba(255,255,255,0.08)',
            text: '#FFFFFF',
            muted: 'rgba(255,255,255,0.80)',
            shadow: '0 12px 28px rgba(0,0,0,0.35)',
          }
        : {
            surface: 'rgba(255,255,255,0.72)',
            surfaceSolid: 'rgba(255,255,255,0.92)',
            border: 'rgba(2,6,23,0.08)',
            text: '#0B1220',
            muted: 'rgba(17,24,39,0.72)',
            shadow: '0 14px 30px rgba(2,6,23,0.12)',
          },
    [isDark]
  );

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/portfolio', label: 'Work' },
    { path: '/features', label: 'Features' },
    { path: '/about', label: 'About' },
    { path: '/blog', label: 'Blog' },
    { path: '/contact', label: 'Contact' },
  ];

  const menuVariants = {
    hidden: { opacity: 0, y: -12 },
    show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: EASE } },
    exit: { opacity: 0, y: -12, transition: { duration: 0.2, ease: EASE } },
  };

  return (
    <header className="sticky top-0 z-50">
      {/* DO: set real defaults here (no recursive var()) */}
      <style>{`
        :root { --brand: #1FA550; --accent: #06B6D4; }
        .brand-text{
          background-image: linear-gradient(90deg, var(--brand, #1FA550), var(--accent, #06B6D4));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
      `}</style>

      {/* Brand gradient top bar */}
      <div
        className="absolute inset-x-0 top-0 h-[2px]"
        style={{ background: 'linear-gradient(90deg, var(--brand, #1FA550), var(--accent, #06B6D4), var(--brand, #1FA550))' }}
      />

      {/* Glass container */}
      <motion.div
        initial={false}
        animate={scrolled ? { backgroundColor: TOKENS.surfaceSolid, boxShadow: TOKENS.shadow } : { backgroundColor: TOKENS.surface, boxShadow: 'none' }}
        transition={{ duration: 0.25, ease: EASE }}
        className="backdrop-blur-md border-b"
        style={{ borderColor: TOKENS.border }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            {/* Brand */}
            <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease: EASE }} className="flex items-center gap-3">
              <img
                src="/images/header.png"
                alt="MonkeyTech Logo"
                className="h-10 w-auto"
                onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/48?text=MT'; }}
              />
              <Link to="/" className="text-xl sm:text-2xl font-extrabold tracking-tight" style={{ color: TOKENS.text }}>
                {/* PURE gradient text for full word */}
                <span className="brand-text">MonkeyTech</span>
              </Link>
            </motion.div>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1 lg:gap-2">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="group relative px-3 py-2 rounded-md text-sm font-medium"
                    style={{ color: active ? 'var(--brand, #1FA550)' : TOKENS.muted }}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="relative">
                      {item.label}
                      <span
                        className="absolute left-0 -bottom-1 h-[2px] w-full origin-left scale-x-0 group-hover:scale-x-100 transition-transform"
                        style={{ background: 'linear-gradient(90deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}
                      />
                    </span>
                  </Link>
                );
              })}

              <Link
                to="/contact"
                className="ml-2 inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold text-black"
                style={{ background: 'linear-gradient(135deg, var(--brand, #1FA550), var(--accent, #06B6D4))', boxShadow: TOKENS.shadow }}
              >
                Get Started
              </Link>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
                onClick={toggleTheme}
                className="ml-2 inline-flex h-10 w-10 items-center justify-center rounded-full ring-1"
                aria-label="Toggle theme"
                style={{ color: TOKENS.text, borderColor: TOKENS.border, background: isDark ? 'rgba(255,255,255,0.06)' : '#fff', boxShadow: TOKENS.shadow }}
              >
                {isDark ? <FaSun className="text-yellow-400" size={18} /> : <FaMoon className="text-slate-800" size={18} />}
              </motion.button>
            </nav>

            {/* Mobile buttons */}
            <div className="md:hidden flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
                onClick={toggleTheme}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full ring-1"
                aria-label="Toggle theme"
                style={{ color: TOKENS.text, borderColor: TOKENS.border, background: isDark ? 'rgba(255,255,255,0.06)' : '#fff', boxShadow: TOKENS.shadow }}
              >
                {isDark ? <FaSun className="text-yellow-400" size={18} /> : <FaMoon className="text-slate-800" size={18} />}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => setMobileOpen((v) => !v)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full ring-1"
                aria-label="Toggle menu"
                style={{ color: TOKENS.text, borderColor: TOKENS.border, background: isDark ? 'rgba(255,255,255,0.06)' : '#fff', boxShadow: TOKENS.shadow }}
              >
                {isMobileOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Mobile sheet */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div className="fixed inset-0 z-40 md:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/55" onClick={() => setMobileOpen(false)} />
            <motion.nav
              variants={menuVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              className="absolute top-0 left-0 right-0 origin-top rounded-b-2xl"
              style={{ background: TOKENS.surfaceSolid, borderBottom: `1px solid ${TOKENS.border}`, boxShadow: TOKENS.shadow }}
            >
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
                <div className="grid gap-3">
                  {navItems.map((item) => {
                    const active = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block rounded-lg px-4 py-3 text-base font-semibold"
                        style={{
                          color: active ? 'var(--brand, #1FA550)' : TOKENS.muted,
                          background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(2,6,23,0.04)',
                        }}
                      >
                        {item.label}
                      </Link>
                    );
                  })}

                  <Link
                    to="/contact"
                    className="mt-2 inline-flex items-center justify-center px-4 py-3 rounded-xl text-base font-semibold text-black"
                    style={{ background: 'linear-gradient(135deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
